import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useDateTime} from '@hconnect/tuc-processes/fields/dateTime/useDateTime'
import {Alert} from '@hm/fundamental'
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material'
import {Typography, Box} from '@mui/material'
import moment from 'moment'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

export interface DateStepProps {
  /**
   * Optional banner text to display above the calendar.
   * Can be used for important notifications or instructions (e.g., timezone warnings).
   */
  bannerText?: string
  /**
   * Optional lower bound for selectable dates (unconfirmed UE/CC order with a non-zero offset: the
   * rescheduled `variationCodeAdjustedTime`). Dates before this day are disabled in addition to the
   * usual min/max range. Omitted in order intake and normal change orders.
   */
  minimumDate?: string
}

/**
 * Component for the Date step
 * Handles delivery date selection for RMC orders
 */
export const DateStep: React.FC<DateStepProps> = ({bannerText, minimumDate}) => {
  const {t} = useTranslation()
  const {dateTime, setDateTime, minDate, maxDate, error, isDateValid, businessDays} = useDateTime()

  // State for calendar navigation - initialize to selected date's month or current month
  const [currentMonth, setCurrentMonth] = useState(() => {
    return dateTime && dateTime[0] ? moment(dateTime[0]) : moment()
  })

  // Track previous dateTime to avoid unnecessary updates
  const prevDateTimeRef = React.useRef(dateTime)

  // Update calendar month when dateTime changes (e.g., from material selection)
  React.useEffect(() => {
    if (dateTime && dateTime[0] && dateTime !== prevDateTimeRef.current) {
      const newMonth = moment(dateTime[0])
      setCurrentMonth((prevMonth) => {
        // Only update if the new date is in a different month
        return newMonth.isSame(prevMonth, 'month') ? prevMonth : newMonth
      })
      prevDateTimeRef.current = dateTime
    }
  }, [dateTime])

  // Handle date selection
  const handleDateChange = useCallback(
    (date: moment.Moment) => {
      // Track date selection event
      trackEvent('rmcDateSelected', {
        selectedDate: date.format('YYYY-MM-DD'),
        dayOfWeek: date.format('dddd'),
        businessLine: 'RMC'
      })

      // Preserve existing time when changing date
      if (dateTime && dateTime[0]) {
        const existingDateTime = moment(dateTime[0])
        const newDateTime = date
          .clone()
          .hour(existingDateTime.hour())
          .minute(existingDateTime.minute())
          .second(existingDateTime.second())
        setDateTime([newDateTime])
      } else {
        setDateTime([date])
      }
    },
    [setDateTime, dateTime]
  )

  // Navigate months
  const goToPreviousMonth = () => setCurrentMonth((prev) => prev.clone().subtract(1, 'month'))
  const goToNextMonth = () => setCurrentMonth((prev) => prev.clone().add(1, 'month'))

  // Check if date should be disabled
  const shouldDisableDate = useCallback(
    (date: moment.Moment) => {
      // Create a fresh instance to resolve moment-business-days caching issue
      const newDateInstance = moment(date.format('YYYY-MM-DD'))

      // Check if date is outside min/max range
      if (minDate && date.isBefore(minDate, 'day')) return true
      if (maxDate && date.isAfter(maxDate, 'day')) return true

      // Floor the first selectable date at the rescheduled delivery time for unconfirmed UE/CC
      // orders (no-op when `minimumDate` is undefined).
      if (minimumDate && date.isBefore(moment(minimumDate), 'day')) return true

      // Check if date is valid according to business rules
      if (businessDays && !isDateValid(newDateInstance)) return true

      return false
    },
    [minDate, maxDate, minimumDate, businessDays, isDateValid]
  )

  // Generate calendar days
  const generateCalendarDays = (): moment.Moment[] => {
    const startOfMonth = currentMonth.clone().startOf('month')
    const startOfWeek = startOfMonth.clone().startOf('isoWeek') // Start with Monday
    const endOfMonth = currentMonth.clone().endOf('month')
    const endOfWeek = endOfMonth.clone().endOf('isoWeek')

    const days: moment.Moment[] = []
    const current = startOfWeek.clone()

    while (current.isSameOrBefore(endOfWeek, 'day')) {
      days.push(current.clone())
      current.add(1, 'day')
    }

    return days
  }

  const calendarDays = generateCalendarDays()
  const numberOfWeeks = Math.ceil(calendarDays.length / 7)
  const calendarHeight: Record<number, string> = {4: '166px', 5: '208px', 6: '250px'}
  const weekDays = [
    t('orderIntake.rmc.steps.date.weekDays.monday', 'Mon'),
    t('orderIntake.rmc.steps.date.weekDays.tuesday', 'Tue'),
    t('orderIntake.rmc.steps.date.weekDays.wednesday', 'Wed'),
    t('orderIntake.rmc.steps.date.weekDays.thursday', 'Thu'),
    t('orderIntake.rmc.steps.date.weekDays.friday', 'Fri'),
    t('orderIntake.rmc.steps.date.weekDays.saturday', 'Sat'),
    t('orderIntake.rmc.steps.date.weekDays.sunday', 'Sun')
  ]

  // Check if day is selected
  const isSelected = (day: moment.Moment) => {
    return dateTime && dateTime[0] && day.isSame(moment(dateTime[0]), 'day')
  }

  // Check if day is in current month
  const isCurrentMonth = (day: moment.Moment) => {
    return day.isSame(currentMonth, 'month')
  }

  // Check if day is weekend
  const isWeekend = (day: moment.Moment) => {
    return day.day() === 6 || day.day() === 0 // Saturday or Sunday
  }

  // Check if day is today
  const isToday = (day: moment.Moment) => {
    return day.isSame(moment(), 'day')
  }

  return (
    <Box sx={{width: '100%'}} data-test-id="date-step-content">
      {/* Banner Text - display important notifications */}
      {bannerText && (
        <Box sx={{marginBottom: '16px'}}>
          <Alert variant="warning" data-test-id="date-banner">
            {bannerText}
          </Alert>
        </Box>
      )}

      {/* Calendar Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '40px',
          marginBottom: '8px'
        }}
      >
        <Box
          onClick={goToPreviousMonth}
          data-test-id="date-previous-month"
          sx={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <ArrowBackIos sx={{fontSize: '16px', color: '#29333d'}} />
        </Box>

        <Typography
          data-test-id="date-month-name"
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            color: '#29333d',
            textAlign: 'center'
          }}
        >
          {t(
            `orderIntake.rmc.steps.date.months.${currentMonth
              .locale('en')
              .format('MMMM')
              .toLowerCase()}`,
            currentMonth.format('MMMM')
          )}{' '}
          {currentMonth.format('YYYY')}
        </Typography>

        <Box
          onClick={goToNextMonth}
          data-test-id="date-next-month"
          sx={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <ArrowForwardIos sx={{fontSize: '16px', color: '#29333d'}} />
        </Box>
      </Box>

      {/* Calendar Content */}
      <Box>
        {/* Month Header */}
        <Box
          sx={{
            display: 'flex',
            gap: '2px',
            marginBottom: '8px'
          }}
        >
          {weekDays.map((day) => (
            <Box
              key={day}
              sx={{
                flex: '1 1 0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: day === 'Sat' || day === 'Sun' ? '#8599ad' : '#52667a',
                  textAlign: 'center'
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{height: calendarHeight[numberOfWeeks]}}>
          <Box
            data-test-id="date-days-table"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              height: '100%'
            }}
          >
            {/* Render weeks */}
            {Array.from({length: numberOfWeeks}, (_, weekIndex) => (
              <Box
                key={weekIndex}
                sx={{
                  flex: '1 1 0',
                  display: 'flex',
                  gap: '2px',
                  alignItems: 'center'
                }}
              >
                {/* Render days in week */}
                {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map((day) => {
                  const disabled = shouldDisableDate(day)
                  const selected = isSelected(day)
                  const currentMonthDay = isCurrentMonth(day)
                  const weekend = isWeekend(day)
                  const today = isToday(day)

                  return (
                    <Box
                      key={day.format('YYYY-MM-DD')}
                      data-test-id={`date-day-${day.format('YYYY-MM-DD')}${
                        disabled ? '' : '-enabled'
                      }${selected ? '-selected' : ''}`}
                      sx={{
                        flex: '1 1 0',
                        height: '100%',
                        cursor: disabled ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => !disabled && handleDateChange(day)}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: selected ? '#016ad4' : 'transparent',
                          '&:hover': {
                            backgroundColor: !disabled && !selected ? '#f5f5f5' : undefined
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: selected
                              ? '#ffffff'
                              : today && currentMonthDay
                                ? '#016ad4'
                                : !currentMonthDay || weekend
                                  ? '#8599ad'
                                  : '#29333d',
                            opacity: disabled ? 0.5 : 1
                          }}
                        >
                          {day.format('D')}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert variant="error" data-test-id="date-error-alert">
          {error.message || t('orderIntake.rmc.steps.date.selectValidDate')}
        </Alert>
      )}
    </Box>
  )
}
